import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import type {SetFieldsMapper} from '@github-ui/optimistic-connection/pendingOperations'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {IssueListItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'

import {mapIssueViewerFieldsToIssueRow} from '../hooks/use-dashboard-issue-optimistic-updates'
import {
  applyRepoIssuesBulkOptimisticUpdate,
  type RepoIssuesBulkOptimisticUpdate,
  type RepoIssuesBulkUpdate,
} from './repo-issues-bulk-update'

const ID_PATH = ['id']
const REPO_ISSUES_BULK_FIELDS = 'repoIssuesBulkFields'

export function useOptimisticRepoIssuesBulkUpdates<
  T extends {itemType: 'issue' | 'pull_request'; id: number | bigint | string; globalId?: string},
>(issues: T[]): T[] {
  return useOptimisticData(issues, {identityPath: ID_PATH, mapFields: mapRepoIssuesFields})
}

const mapRepoIssuesFields: SetFieldsMapper = fields => {
  const mappedFields = mapIssueViewerFieldsToIssueRow(fields)
  const bulkFields = fields[REPO_ISSUES_BULK_FIELDS]
  if (!bulkFields || typeof bulkFields !== 'object' || Array.isArray(bulkFields)) return mappedFields
  return {...mappedFields, ...bulkFields}
}

export function repoIssuesBulkOptimisticOperation(
  issue: IssueListItem,
  update: RepoIssuesBulkUpdate,
  optimisticUpdate?: RepoIssuesBulkOptimisticUpdate,
): SetFieldsOperation {
  return {
    type: 'setFields',
    id: getListItemIdentity(issue),
    fields: {[REPO_ISSUES_BULK_FIELDS]: optimisticFields(issue, update, optimisticUpdate)},
  }
}

function optimisticFields(
  issue: IssueListItem,
  update: RepoIssuesBulkUpdate,
  optimisticUpdate?: RepoIssuesBulkOptimisticUpdate,
): Partial<IssueListItem> {
  const optimistic = applyRepoIssuesBulkOptimisticUpdate(issue, update, optimisticUpdate)
  if ('state' in update) {
    return {
      state: optimistic.state,
      stateReason: optimistic.stateReason,
      closed: optimistic.closed,
      closedAt: optimistic.closedAt,
      // The old status no longer matches the new state, so show the state icon until the server row arrives.
      status: undefined,
    }
  }
  if ('addedLabelIds' in update) return {labels: optimistic.labels}
  if ('applyAssigneeIds' in update) return {assignees: optimistic.assignees}
  if ('milestoneId' in update || 'clearMilestone' in update) return {milestone: optimistic.milestone}
  if ('issueTypeId' in update || 'clearIssueType' in update) return {issueType: optimistic.issueType}
  return {}
}
